<template>
  <div class="public-shop">
    <header class="shop-header" :style="{ background: themeColor }">
      <div class="container py-3">
        <router-link :to="coursePath" class="text-white header-link">
          <i class="fas fa-arrow-left"></i> Kembali ke Kursus
        </router-link>
      </div>
    </header>

    <div class="container mt-4 pb-5">
      <div v-if="loading" class="card p-5 text-center">
        <div class="spinner-border text-primary"></div>
        <p class="text-muted mt-3 mb-0">Memuat status pendaftaran...</p>
      </div>

      <template v-else-if="enrollment">
        <!-- Status hero -->
        <div class="text-center mb-4 status-hero">
          <div v-if="isPaid" class="text-success">
            <i class="fas fa-check-circle status-icon"></i>
            <h3>Pembayaran Berhasil!</h3>
            <p class="text-muted">Selamat! Anda sudah terdaftar di kursus ini.</p>
          </div>
          <div v-else-if="isFree">
            <i class="fas fa-graduation-cap text-primary status-icon"></i>
            <h3>Pendaftaran Berhasil!</h3>
            <p class="text-muted">Kursus gratis — Anda bisa langsung mulai belajar.</p>
          </div>
          <div v-else>
            <i class="fas fa-clock text-warning status-icon"></i>
            <h3>Menunggu Pembayaran</h3>
            <p class="text-muted">Selesaikan pembayaran untuk mengakses kursus.</p>
          </div>
        </div>

        <!-- Enrollment summary -->
        <div class="card p-4 mb-3 shadow-sm">
          <h5 class="mb-3"><i class="fas fa-book-open text-primary"></i> Detail Pendaftaran</h5>
          <div class="row detail-row">
            <div class="col-sm-4 text-muted">Kursus</div>
            <div class="col-sm-8"><b>{{ course && course.title }}</b></div>
          </div>
          <div class="row detail-row" v-if="enrollment.student_name">
            <div class="col-sm-4 text-muted">Nama Siswa</div>
            <div class="col-sm-8">{{ enrollment.student_name }}</div>
          </div>
          <div class="row detail-row" v-if="enrollment.student_phone">
            <div class="col-sm-4 text-muted">WhatsApp</div>
            <div class="col-sm-8">{{ enrollment.student_phone }}</div>
          </div>
          <div class="row detail-row">
            <div class="col-sm-4 text-muted">No. Pendaftaran</div>
            <div class="col-sm-8">
              <code>{{ enrollment.enrollment_number }}</code>
              <button class="btn btn-sm btn-link p-0 ml-2" @click="copyText(enrollment.enrollment_number)">
                <i class="fas fa-copy"></i>
              </button>
            </div>
          </div>
          <div class="row detail-row" v-if="enrollment.invoice_number">
            <div class="col-sm-4 text-muted">Invoice</div>
            <div class="col-sm-8">{{ enrollment.invoice_number }}</div>
          </div>
          <div class="row detail-row" v-if="!isFree">
            <div class="col-sm-4 text-muted">Total</div>
            <div class="col-sm-8"><b class="text-primary">{{ formatRupiah(enrollment.amount) }}</b></div>
          </div>
          <div class="row detail-row">
            <div class="col-sm-4 text-muted">Status</div>
            <div class="col-sm-8">
              <span class="badge" :class="paymentBadgeClass">{{ paymentStatusLabel }}</span>
            </div>
          </div>
          <div class="row detail-row" v-if="paymentMethodLabel">
            <div class="col-sm-4 text-muted">Metode Bayar</div>
            <div class="col-sm-8">{{ paymentMethodLabel }}</div>
          </div>
        </div>

        <!-- Paid / free: start learning -->
        <div v-if="isPaid || isFree" class="card p-4 mb-3 shadow-sm text-center">
          <a :href="accessUrl" class="btn btn-primary btn-lg btn-block">
            <i class="fas fa-play"></i> Mulai Belajar
          </a>
          <p class="text-muted small mt-3 mb-0">Link akses juga dikirim ke WhatsApp &amp; email Anda.</p>
        </div>

        <!-- Pending payment -->
        <div v-else-if="isPending" class="card p-4 mb-3 shadow-sm">
          <h5 class="mb-3"><i class="fas fa-credit-card text-primary"></i> Lanjutkan Pembayaran</h5>

          <!-- Manual transfer -->
          <div v-if="isManualTransfer">
            <p class="mb-2">Transfer ke rekening berikut:</p>
            <div class="bank-box" v-if="bankAccount">
              <pre class="mb-0">{{ bankAccount }}</pre>
              <button class="btn btn-sm btn-outline-secondary mt-2" @click="copyText(bankAccount)">
                <i class="fas fa-copy"></i> Salin Rekening
              </button>
            </div>
            <p v-else class="text-muted">Hubungi admin toko untuk detail rekening transfer.</p>
            <p class="small text-muted mt-3 mb-0">
              Setelah transfer, kirim bukti via WhatsApp ke admin.
            </p>
            <a v-if="waBuktiLink" :href="waBuktiLink" target="_blank" class="btn btn-success btn-block mt-3">
              <i class="fab fa-whatsapp"></i> Kirim Bukti Transfer via WA
            </a>
          </div>

          <!-- Tripay -->
          <div v-else>
            <div v-if="tripayPaymentName" class="alert alert-light border mb-3">
              <small class="text-muted d-block">Metode</small>
              <b>{{ tripayPaymentName }}</b>
            </div>

            <!-- VA / pay code -->
            <div v-if="payCode" class="pay-code-box mb-3">
              <small class="text-muted d-block mb-1">{{ payCodeLabel }}</small>
              <div class="d-flex align-items-center">
                <code class="pay-code flex-grow-1">{{ payCode }}</code>
                <button class="btn btn-outline-primary btn-sm ml-2" @click="copyText(payCode)">
                  <i class="fas fa-copy"></i> Salin
                </button>
              </div>
            </div>

            <!-- QRIS -->
            <div v-if="showQr" class="text-center mb-3">
              <img :src="qrImageUrl" alt="QR Code Pembayaran" class="qr-image" />
              <p class="small text-muted mt-2">Scan QR code di atas untuk bayar</p>
            </div>

            <a
              v-if="paymentUrl"
              :href="paymentUrl"
              target="_blank"
              class="btn btn-primary btn-lg btn-block mb-2"
            >
              💳 Bayar Sekarang
            </a>
            <button
              v-else
              class="btn btn-primary btn-lg btn-block mb-2"
              :disabled="fetchingPayment"
              @click="getPaymentLink"
            >
              <span v-if="fetchingPayment"><i class="fas fa-spinner fa-spin"></i> Memproses...</span>
              <span v-else>💳 Dapatkan Link Bayar</span>
            </button>

            <p v-if="expiredLabel" class="small text-muted text-center mb-0">
              <i class="fas fa-hourglass-half"></i> {{ expiredLabel }}
            </p>
          </div>
        </div>

        <div class="text-center">
          <router-link :to="shopPath('')" class="btn btn-light">Kembali ke Toko</router-link>
        </div>

        <div v-if="isPending" class="text-center text-muted small mt-3">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': polling }"></i>
          Halaman ini auto-refresh setiap 5 detik untuk update status.
        </div>
      </template>

      <div v-else class="card p-5 text-center">
        <i class="fas fa-exclamation-circle text-danger" style="font-size:48px;"></i>
        <h4 class="mt-3">Pendaftaran tidak ditemukan</h4>
        <router-link :to="shopPath('')" class="btn btn-primary mt-3">Kembali ke Toko</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import publicShopPaths from './public_shop_paths';
import { applyShopTheme, shopHeaderBackground, pickShopTheme } from '@/components/shop_public/shop_public_utils';
import { trackPurchase } from '@/components/shop_public/shop_tracking';
import '@/components/shop_public/shop_public.css';

export default {
  mixins: [publicShopPaths],
  data() {
    return {
      loading: true,
      polling: false,
      fetchingPayment: false,
      enrollment: null,
      course: null,
      accessUrl: '',
      isFree: false,
      shopInfo: null,
      tripay: null,
      pollTimer: null,
      prevPaymentStatus: null,
      wasPaidAnnounced: false,
    };
  },
  computed: {
    themeColor() {
      return shopHeaderBackground(pickShopTheme(this.shopInfo, this.shop));
    },
    shop() {
      return this.$store.state.public_shop.shop;
    },
    bankAccount() {
      const fromShop = this.shopInfo?.bank_account || this.shop?.bank_account;
      return fromShop || '';
    },
    ownerPhone() {
      return this.shopInfo?.owner_phone || this.shop?.owner_phone || '';
    },
    coursePath() {
      const slug = this.course?.slug || this.$route.params.courseSlug;
      return this.shopPath('/kursus/' + slug);
    },
    isPaid() {
      return this.enrollment?.payment_status === 'paid';
    },
    isPending() {
      return this.enrollment?.payment_status === 'pending';
    },
    isManualTransfer() {
      return this.enrollment?.payment_method === 'transfer_manual';
    },
    paymentUrl() {
      return this.enrollment?.payment_url || this.tripay?.checkout_url || this.tripay?.pay_url || '';
    },
    payCode() {
      return this.tripay?.pay_code || '';
    },
    tripayPaymentName() {
      return this.tripay?.payment_name || '';
    },
    showQr() {
      const pm = (this.enrollment?.payment_method || '').toUpperCase();
      const name = (this.tripay?.payment_name || '').toUpperCase();
      return this.payCode && (pm.includes('QRIS') || name.includes('QRIS'));
    },
    qrImageUrl() {
      if (!this.payCode) return '';
      return 'https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=' + encodeURIComponent(this.payCode);
    },
    payCodeLabel() {
      const name = (this.tripay?.payment_name || '').toUpperCase();
      if (name.includes('QRIS')) return 'QR Code';
      if (name.includes('VA') || name.includes('VIRTUAL')) return 'Nomor Virtual Account';
      return 'Kode Pembayaran';
    },
    paymentStatusLabel() {
      const map = {
        pending: 'Menunggu Pembayaran',
        paid: 'Lunas',
        expired: 'Kedaluwarsa',
        failed: 'Gagal',
      };
      return map[this.enrollment?.payment_status] || this.enrollment?.payment_status;
    },
    paymentBadgeClass() {
      const s = this.enrollment?.payment_status;
      return {
        'badge-warning': s === 'pending',
        'badge-success': s === 'paid',
        'badge-danger': s === 'failed' || s === 'expired',
        'badge-secondary': !['pending', 'paid', 'failed', 'expired'].includes(s),
      };
    },
    paymentMethodLabel() {
      const m = this.enrollment?.payment_method;
      if (!m) return '';
      if (m === 'transfer_manual') return 'Transfer Bank Manual';
      if (this.tripayPaymentName) return this.tripayPaymentName;
      if (m.startsWith('tripay_')) return m.replace('tripay_', '').toUpperCase();
      return m;
    },
    expiredLabel() {
      const exp = this.enrollment?.payment_expired_at || (this.tripay?.expired_time ? this.tripay.expired_time * 1000 : null);
      if (!exp) return '';
      const d = new Date(exp);
      if (Number.isNaN(d.getTime())) return '';
      return 'Batas waktu: ' + d.toLocaleString('id-ID');
    },
    waBuktiLink() {
      if (!this.ownerPhone) return '';
      const n = this.ownerPhone.replace(/^0/, '62').replace(/[^\d]/g, '');
      const text = encodeURIComponent(
        'Halo, saya kirim bukti transfer untuk pendaftaran kursus ' +
        (this.course?.title || '') + ' — ' + this.enrollment.enrollment_number
      );
      return `https://wa.me/${n}?text=${text}`;
    },
  },
  async mounted() {
    if (!this.effectiveSlug && this.onCustomDomain) {
      await this.$store.dispatch('public_shop/public_get_shop_by_domain');
    }
    await this.$store.dispatch('public_shop/public_get_shop', this.effectiveSlug);
    await this.load();
    this.pollTimer = setInterval(this.pollLoad, 5000);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    async load() {
      try {
        const num = this.$route.params.enrollmentNumber;
        const r = await this.$store.dispatch('public_shop/public_get_enrollment', num);
        const data = r.data.data;
        this.enrollment = data.enrollment;
        this.course = data.course;
        this.accessUrl = data.access_url;
        this.isFree = data.is_free || data.course?.is_free || this.enrollment?.amount === 0;
        this.shopInfo = data.shop || null;
        this.tripay = data.tripay || null;
        applyShopTheme(pickShopTheme(this.shopInfo, this.shop));
        if (this.enrollment && this.enrollment.payment_status === 'paid') {
          trackPurchase(
            { order_id: this.enrollment.enrollment_number, total_amount: this.enrollment.amount },
            [{ product_id: this.course?.id, product_name: this.course?.title, quantity: 1, subtotal: this.enrollment.amount }],
          );
        }
        if (this.prevPaymentStatus === null) {
          this.prevPaymentStatus = this.enrollment?.payment_status;
        }
      } catch (e) {
        this.enrollment = null;
      } finally {
        this.loading = false;
      }
    },
    async pollLoad() {
      if (this.isPaid || this.isFree) {
        if (this.pollTimer) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
        }
        return;
      }
      this.polling = true;
      const prev = this.enrollment?.payment_status;
      try {
        await this.load();
        const now = this.enrollment?.payment_status;
        if (prev === 'pending' && now === 'paid' && !this.wasPaidAnnounced) {
          this.wasPaidAnnounced = true;
          this.$toast.success('Pembayaran dikonfirmasi! Silakan mulai belajar.');
          trackPurchase(
            { order_id: this.enrollment.enrollment_number, total_amount: this.enrollment.amount },
            [{ product_id: this.course?.id, product_name: this.course?.title, quantity: 1, subtotal: this.enrollment.amount }],
          );
          if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
          }
        }
      } finally {
        this.polling = false;
      }
    },
    async getPaymentLink() {
      if (!this.enrollment?.payment_method) {
        this.$toast.error('Metode pembayaran tidak tersedia');
        return;
      }
      this.fetchingPayment = true;
      try {
        const r = await this.$store.dispatch('public_shop/public_get_enrollment_payment', {
          enrollmentNumber: this.enrollment.enrollment_number,
          method: this.enrollment.payment_method,
        });
        const d = r.data.data;
        if (d.payment_url) {
          window.open(d.payment_url, '_blank');
        }
        if (d.pay_code || d.payment_name) {
          this.tripay = {
            ...(this.tripay || {}),
            pay_code: d.pay_code || this.tripay?.pay_code,
            payment_name: d.payment_name || this.tripay?.payment_name,
            checkout_url: d.payment_url,
          };
        }
        await this.load();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Gagal generate link bayar');
      } finally {
        this.fetchingPayment = false;
      }
    },
    formatRupiah(v) {
      return 'Rp ' + (v || 0).toLocaleString('id-ID');
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$toast.success('Disalin ke clipboard');
      } catch (e) {
        this.$toast.error('Gagal menyalin');
      }
    },
  },
};
</script>

<style scoped>
.public-shop {
  background: #f4f6f8;
  min-height: 100vh;
}
.shop-header {
  color: white;
}
.header-link {
  text-decoration: none;
}
.status-icon {
  font-size: 64px;
  display: block;
  margin-bottom: 12px;
}
.detail-row {
  margin-bottom: 10px;
}
.detail-row:last-child {
  margin-bottom: 0;
}
.bank-box {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
}
.bank-box pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  background: transparent;
  border: none;
  padding: 0;
}
.pay-code-box {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
}
.pay-code {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 1px;
  word-break: break-all;
}
.qr-image {
  width: 220px;
  height: 220px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 8px;
  background: white;
}
</style>
